import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React from 'react'

import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router-dom'
import { CONTENT_MAX_WIDTH } from '../../config'
import { isDevelopmentEnvironment } from '../../config/getEnvVars'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { AppBarComponent } from '../AppBar'
import { FooterBarComponent } from '../FooterBar'
import { HomeBottomRowLeftComponent } from './BottomRow/left'
import { HomeBottomRowRightComponent } from './BottomRow/right'
import { HomeTopRowComponent } from './TopRow'

export const HomeComponent: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const isDevelopmentSite = React.useMemo(() => isDevelopmentEnvironment(), [])
  const { height, width } = useDimensions()
  const topbarHeight = getTopbarHeight(width)
  const onChangeRoute = React.useCallback(
    (path: string) => {
      const pathname = location.pathname
      if (path !== pathname) navigate(path)
    },
    [navigate, location]
  )

  return (
    <>
      <Helmet>
        <title>Home || JustLockIt</title>
        <meta name="keywords" content="HTML,CSS,JavaScript" />
        <meta
          name="description"
          content="justlockit.com home page. storage units Schulenburg, Texas"
        />
      </Helmet>
      <AppBarComponent />
      <Box
        sx={{
          width: '100%',
          height,
          overflow: 'auto',
          padding: 2,
        }}
        className="home-container"
      >
        {isDevelopmentSite ? (
          <Box sx={{ mb: 2 }} className="development-alert">
            <Alert
              severity="error"
              variant="filled"
              sx={{
                alignItems: 'center',
                border: '3px solid',
                borderColor: 'error.light',
                boxShadow: '0 0 0 4px rgba(244, 67, 54, 0.25)',
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                This is a test site only
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                You are on the development environment at justlockit-dev.com. Do
                not create real customer accounts, and do not expect live
                reservations or payments to work here.
              </Typography>
            </Alert>
          </Box>
        ) : null}
        <Box
          className="home-grid"
          sx={{
            maxWidth: CONTENT_MAX_WIDTH,
            margin: 'auto',
            marginTop: `${topbarHeight}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box className="home-top-row">
            <HomeTopRowComponent onChangeRoute={onChangeRoute} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box
              sx={{
                minHeight: { xs: 'fit-content', sm: 'fit-content', md: 450 },
                display: 'flex',
              }}
              className="home-bottom-row-left"
            >
              <HomeBottomRowLeftComponent onChangeRoute={onChangeRoute} />
            </Box>
            <Box
              sx={{
                minHeight: { xs: 'fit-content', sm: 'fit-content', md: 450 },
                display: 'flex',
              }}
              className="home-bottom-row-right"
            >
              <HomeBottomRowRightComponent />
            </Box>
          </Box>
        </Box>
      </Box>
      <FooterBarComponent />
    </>
  )
}
